import { Component, Input, OnInit, AfterViewInit, ElementRef, ViewChild, PLATFORM_ID, OnDestroy, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';

export interface TabItem {
  id: string;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'umkt-tabs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
})
export class TabsComponent implements OnInit, AfterViewInit, OnDestroy  {
  @Input() tabs: TabItem[] = [];
  @Input() activeTabId: string = '';
  @Output() tabChange = new EventEmitter<string>();

  @ViewChild('tabsScrollContainer') tabsScrollContainer!: ElementRef;

  private destroy$ = new Subject<void>();
  private platformId = inject(PLATFORM_ID);


  ngOnInit() {
    if (!this.activeTabId && this.tabs.length > 0) {
      this.activeTabId = this.tabs[0].id;
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.scrollToActiveTab();
      }, 100);
    }
  }

  onTabClick(tabId: string): void {
    if (this.activeTabId !== tabId) {
      this.activeTabId = tabId;
      this.tabChange.emit(tabId);

      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          this.scrollToActiveTab();
        }, 50);
      }
    }
  }

  isActive(tabId: string): boolean {
    return this.activeTabId === tabId;
  }

  isDisabled(tab: TabItem): boolean {
    return tab.disabled || false;
  }

  trackByTabId(index: number, tab: TabItem): string {
    return tab.id;
  }

  // Public method to scroll to active tab (useful for external components)
  scrollToActiveTabPublic(): void {
    this.scrollToActiveTab();
  }

  // Public method to reset scroll to beginning
  resetToBeginning(): void {
    if (!this.tabsScrollContainer || !isPlatformBrowser(this.platformId))
      return;

    const scrollContainer = this.tabsScrollContainer.nativeElement;
    
    if (scrollContainer) {
      if (typeof scrollContainer.scrollTo === 'function') {
        scrollContainer.scrollTo({
          left: 0,
          behavior: 'smooth',
        });
      } else {
        scrollContainer.scrollLeft = 0;
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private scrollToActiveTab(): void {
    if (!this.tabsScrollContainer || !isPlatformBrowser(this.platformId))
      return;

    const scrollContainer = this.tabsScrollContainer.nativeElement;
    const activeTab = scrollContainer.querySelector('.tab-item.active');

    if (activeTab && scrollContainer) {
      const containerWidth = scrollContainer.offsetWidth;
      const tabLeft = activeTab.offsetLeft;
      const tabWidth = activeTab.offsetWidth;

      const scrollPosition = tabLeft - containerWidth / 2 + tabWidth / 2;

      const targetScrollLeft = Math.max(0, scrollPosition);

      if (typeof scrollContainer.scrollTo === 'function') {
        scrollContainer.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth',
        });
      } else {
        scrollContainer.scrollLeft = targetScrollLeft;
      }
    }
  }
}
